import React, { useState, useEffect } from "react";
import { Loader2, Save, X } from "lucide-react";
import { tableList } from "../../../../services/tableService";
import { postList } from "../../../../services/postService";
import { orderAddEdit, orderDetails } from "../../../../services/orderService";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import SearchableDropdown from "./SearchableDropdown";
import { useNavigate, useParams } from "react-router-dom";

const ManageOrder = () => {
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();
  const id = params?.id;

  const [selectedTable, setSelectedTable] = useState(null);
  const [numberOfPersons, setNumberOfPersons] = useState(null);
  const [items, setItems] = useState([{ product: null, variant: null, qty: 0, price: 0 }]);

  const [tableError, setTableError] = useState("");
  const [itemsError, setItemsError] = useState("");

  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: async () => {
      const res = await tableList({ limit: 10000 });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs?.data;
    },
  });

  const { data: foodItems } = useQuery({
    queryKey: ["food-items"],
    queryFn: async () => {
      const res = await postList({ limit: 10000, type: "product" });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs?.data;
    },
  });

  const { data: orderDetail } = useQuery({
    queryKey: ["order-detail", id],
    queryFn: async () => {
      const res = await orderDetails({ id });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (orderDetail?._id && foodItems?.length) {
      const t = orderDetail.tableData;
      setSelectedTable(t ? { _id: t._id, tableNumber: t.tableNumber, tableName: t.tableName } : null);
      setNumberOfPersons(orderDetail.numberOfPersons || orderDetail.numberOfPerson || "");

      const mappedItems = orderDetail.items.map((it) => {
        const matchedProduct = foodItems.find((f) => f._id === it.product);
        let matchedVariant = null;
        if (matchedProduct?.variants?.length) {
          matchedVariant =
            matchedProduct.variants.find((v) => v.slug === it.variant) || matchedProduct.variants.find((v) => v.name === it.variantName) || null;
        }

        return {
          product: matchedProduct
            ? {
                label: matchedProduct.content.en.title,
                value: matchedProduct._id,
                image: matchedProduct.coverMedia?.url,
                variants: matchedProduct.variants || [],
              }
            : { label: it.productName, value: it.product, variants: [] },
          variant: matchedVariant
            ? {
                label: `${matchedVariant.name} ( ${matchedVariant.price.toFixed(2)})`,
                value: matchedVariant.slug,
                price: matchedVariant.price,
              }
            : { label: it.variantName, value: it.variant, price: it.price },
          qty: it.qty,
          price: it.price,
        };
      });

      setItems(mappedItems);
    }
  }, [orderDetail, foodItems]);

  const { mutate: placeOrder, isLoading: placing } = useMutation({
    mutationFn: async (payload) => await orderAddEdit(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["order-detail", id] });
      if (res?.status === 201) toast.success("Order placed successfully!");
      else if (res?.status === 200) toast.success("Order updated successfully!");
      else toast.warning(res?.message || "Something went wrong!");
      navigate("/admin/order");
    },
    onError: () => toast.error("❌ Failed to save order!"),
  });

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        if (field === "qty") {
          let val = value.replace(/^0+(?!$)/, "");
          if (val === "") val = "0";
          return { ...it, qty: parseInt(val, 10) };
        }
        return { ...it, [field]: value };
      })
    );
  };

  const adjustQty = (index, delta) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        const qty = Math.max(0, (parseInt(it.qty, 10) || 0) + delta);
        return { ...it, qty };
      })
    );
  };

  const addItem = () => setItems([...items, { product: null, variant: null, qty: 0, price: 0 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const totalAmount = items.reduce((sum, i) => sum + (i.price || 0) * (i.qty || 0), 0);

  const handlePlaceOrder = () => {
    let valid = true;
    setTableError("");
    setItemsError("");

    if (!selectedTable?._id) {
      setTableError("Select a table");
      valid = false;
    }
    const hasValidItems = items.some((it) => it.product && it.variant && it.qty > 0);
    if (!hasValidItems) {
      setItemsError("At least one valid item is required");
      valid = false;
    }

    if (!valid) return;

    const payload = {
      organizationId: "68fb43e203b90c45f92756c2",
      numberOfPerson: Number(numberOfPersons),
      tableId: selectedTable._id,
      items: items.map((it) => ({
        productId: it.product.value,
        variant: it.variant.value,
        qty: it.qty,
      })),
      ...(id && { id }),
    };

    placeOrder(payload);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center sticky top-0 z-10 flex-col xls:flex-row space-y-4">
        <h2 className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left w-full">
          {id ? "Edit Order" : "Manage Order"}
        </h2>
        <div className="flex gap-3 w-full justify-center md:justify-end">
          <button
            onClick={handlePlaceOrder}
            disabled={placing}
            className="flex items-center gap-2 min-w-24 bg-black text-white px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {placing ? "Saving..." : id ? "Update Order" : "Place Order"}
          </button>

          <button
            className="flex items-center gap-2 min-w-24 border border-primary text-primary px-4 py-2 rounded-md hover:bg-primary/5"
            onClick={() => navigate("/admin/order")}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>

      <div className="p-0">
        <div className="rounded-lg grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-500 mb-2">
              Table Number <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={
                tables?.map((t) => ({
                  label: `Table ${t.tableNumber} - ${t.tableName}`,
                  value: t._id,
                  tableNumber: t.tableNumber,
                  tableName: t.tableName,
                  disabled: !!t.orderStatus,
                })) || []
              }
              placeholder="Choose Table"
              selectedValue={
                selectedTable
                  ? {
                      label: `Table ${selectedTable.tableNumber} - ${selectedTable.tableName}`,
                      value: selectedTable._id,
                    }
                  : null
              }
              onSelect={(opt) => {
                if (opt?.disabled) return;
                setSelectedTable({
                  _id: opt.value,
                  tableNumber: opt.tableNumber,
                  tableName: opt.tableName,
                });
                setTableError("");
              }}
              searchKeys={["label", "tableNumber", "tableName"]}
              displaySearch={true}
              startIconClass="fa-solid fa-utensils"
              iconColor="red-900"
            />

            {tableError && <p className="text-red-500 text-sm mt-1">{tableError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Number of Persons</label>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 bg-white">
              <i className="fa-solid fa-user text-blue-700 text-lg mr-3"></i>
              <input
                type="number"
                placeholder="Enter number of persons"
                className="w-full outline-none text-gray-700 placeholder-gray-400 bg-transparent border-none"
                value={numberOfPersons ?? 0}
                onChange={(e) => setNumberOfPersons(e.target.value)}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-md font-semibold text-gray-500 mt-4 mb-1">Order Items</label>

            <div className="space-y-4 relative">
              {items.map((item, index) => {
                const variants = item.product?.variants || [];
                return (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center md:justify-between border border-gray-200 rounded-lg p-4 bg-white shadow-sm gap-4 transition-all duration-300"
                  >
                    <div className="w-full md:w-1/3 relative">
                      <SearchableDropdown
                        options={
                          foodItems?.map((f) => ({
                            label: f.content.en.title,
                            value: f._id,
                            image: f.coverMedia?.url,
                            variants: f.variants || [],
                          })) || []
                        }
                        selectedValue={item.product ? { label: item.product.label, value: item.product.value } : null}
                        onSelect={(opt) => {
                          handleItemChange(index, "product", opt);
                          handleItemChange(index, "variant", null);
                          handleItemChange(index, "price", 0);
                        }}
                        placeholder="Select Food"
                        imageKey="image"
                        searchKeys={["label"]}
                        displaySearch={true}
                        iconColor="text-yellow-500"
                      />
                    </div>

                    {item.product?.image && (
                      <div className="flex items-center justify-center w-16 h-16 border rounded-md overflow-hidden">
                        <img src={item.product.image} alt={item.product.label} className="object-cover w-full h-full" />
                      </div>
                    )}

                    <div
                      className={`w-full md:w-1/4 transform transition-all duration-300 ease-in-out origin-top ${
                        item.product ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0 h-0"
                      }`}
                    >
                      {item.product && (
                        <SearchableDropdown
                          options={
                            variants.map((v) => ({
                              label: (
                                <span>
                                  {v.name} - <span className="text-blue-600">${v.price.toFixed(2)}</span>
                                </span>
                              ),
                              value: v.slug,
                              price: v.price,
                            })) || []
                          }
                          selectedValue={item.variant ? { label: item.variant.label, value: item.variant.value } : null}
                          onSelect={(opt) => {
                            handleItemChange(index, "variant", opt);
                            handleItemChange(index, "price", opt.price || 0);
                          }}
                          placeholder="Select Variant"
                          displaySearch={true}
                          startIconClass="fa-solid fa-layer-group"
                          iconColor="text-purple-600"
                        />
                      )}
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-1/4 justify-center">
                      <button
                        type="button"
                        onClick={() => adjustQty(index, -1)}
                        className="border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-100"
                      >
                        <i className="fa-solid fa-minus"></i>
                      </button>
                      <input
                        type="number"
                        min="0"
                        className="w-16 text-center border border-gray-300 rounded-md py-1 focus:outline-none"
                        value={item.qty}
                        onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => adjustQty(index, 1)}
                        className="border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-100"
                      >
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-1/5 justify-between md:justify-end">
                      <span className="text-gray-600">
                        <span className="font-medium">Subtotal:</span>{" "}
                        <span className="text-primary font-semibold">$ {item.qty >= 1 ? (item.price * item.qty).toFixed(2) : "0.00"}</span>
                      </span>
                      <button type="button" onClick={() => removeItem(index)} className="text-red-500 hover:text-red-600">
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}

              <button type="button" onClick={addItem} className="flex items-center gap-2 text-black mt-4 font-medium hover:text-primary/80">
                <i className="fa-solid fa-plus"></i> Add Items
              </button>
            </div>

            {itemsError && <p className="text-red-500 text-sm mt-2">{itemsError}</p>}

            <div className="mt-6 flex justify-end border-t pt-4 text-lg font-semibold text-primary">Total: $ {totalAmount.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageOrder;
